import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Edit,
  MoreHorizontal,
  Percent,
  Trash2,
  User,
  CalendarIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { StageBadge } from "./OpportunitiesPage";

interface SalesStage {
  id: string;
  name: string;
  displayOrder: number;
  defaultProbability: number;
}

interface Account {
  id: string;
  name: string;
  classification?: string;
}

interface Opportunity {
  id: string;
  accountId: string | null;
  categoryId: string | null;
  name: string;
  value: string | null;
  stage: string;
  probability: number;
  expectedCloseDate: string | null;
  salesRepId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  account?: Account;
  salesRep?: { firstName: string | null; lastName: string | null; email: string };
}

const formSchema = z.object({
  name: z.string().min(1, "Opportunity name is required"),
  value: z.string().min(1, "Estimated value is required"),
  stage: z.string().min(1, "Stage is required"),
  probability: z.coerce.number().int().min(0).max(100),
  expectedCloseDate: z.date().optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

function getStageDotColor(stage: string): string {
  const lower = stage.toLowerCase().replace(/\s+/g, "_");
  if (lower === "closed_won") return "bg-green-500";
  if (lower === "closed_lost") return "bg-red-500";
  if (lower === "negotiation") return "bg-amber-500";
  if (lower === "proposal") return "bg-blue-500";
  if (lower === "qualification") return "bg-purple-500";
  return "bg-gray-400";
}

export default function OpportunityDetailPage() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { sessionId } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: opportunity, isLoading } = useQuery<Opportunity>({
    queryKey: ["/api/opportunities", params.id],
    queryFn: () => api.get<Opportunity>(`/opportunities/${params.id}`, sessionId),
    enabled: !!params.id,
  });

  const { data: salesStages = [] } = useQuery<SalesStage[]>({
    queryKey: ["/api/sales-stages"],
    queryFn: () => api.get<SalesStage[]>("/sales-stages", sessionId),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      value: "",
      stage: "Prospecting",
      probability: 10,
      notes: "",
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return api.patch<Opportunity>(
        `/opportunities/${params.id}`,
        {
          name: data.name,
          value: data.value,
          stage: data.stage,
          probability: data.probability,
          expectedCloseDate: data.expectedCloseDate
            ? data.expectedCloseDate.toISOString()
            : null,
          notes: data.notes || null,
        },
        sessionId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/opportunities"] });
      toast({
        title: "Opportunity updated",
        description: "Changes saved successfully.",
      });
      setEditOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update opportunity",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () =>
      api.delete<{ message: string }>(`/opportunities/${params.id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/opportunities"] });
      toast({
        title: "Opportunity deleted",
        description: "The opportunity has been deleted.",
      });
      setLocation("/opportunities");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete opportunity",
        variant: "destructive",
      });
    },
  });

  const handleEditOpen = () => {
    if (opportunity) {
      form.reset({
        name: opportunity.name || "",
        value: String(opportunity.value || ""),
        stage: opportunity.stage || "Prospecting",
        probability: opportunity.probability || 10,
        expectedCloseDate: opportunity.expectedCloseDate
          ? new Date(opportunity.expectedCloseDate)
          : undefined,
        notes: opportunity.notes || "",
      });
    }
    setEditOpen(true);
  };

  const onSubmit = (data: FormData) => {
    updateMutation.mutate(data);
  };

  const handleStageChange = (stageName: string) => {
    form.setValue("stage", stageName);
    const stage = salesStages.find((s) => s.name === stageName);
    if (stage) form.setValue("probability", stage.defaultProbability);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="lg:col-span-2 h-[200px] rounded-lg" />
          <Skeleton className="h-[200px] rounded-lg" />
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Opportunity not found</p>
      </div>
    );
  }

  const stage = opportunity.stage || "Prospecting";
  const weightedValue = Math.round(
    Number(opportunity.value || 0) * (opportunity.probability || 0) / 100,
  );

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLocation("/opportunities")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight">
                {opportunity.name}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-base">
              {opportunity.account && (
                <button
                  onClick={() => {
                    const path =
                      opportunity.account?.classification === "prospect"
                        ? `/leads/${opportunity.accountId}`
                        : `/accounts/${opportunity.accountId}`;
                    setLocation(path);
                  }}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {opportunity.account.name}
                </button>
              )}
              <StageBadge stage={stage} />
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleEditOpen}>
              <Edit className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => {
                    if (
                      confirm(
                        `Are you sure you want to delete "${opportunity.name}"?`,
                      )
                    ) {
                      deleteMutation.mutate();
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Opportunity
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Stage</span>
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  "h-2 w-2 rounded-full shrink-0",
                  getStageDotColor(stage),
                )}
              />
              <span className="text-sm font-medium">{stage}</span>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Expected Close</span>
              <p className="text-sm font-medium">
                {opportunity.expectedCloseDate
                  ? format(new Date(opportunity.expectedCloseDate), "MMM d, yyyy")
                  : "—"}
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border shrink-0" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Value</span>
              <p className="text-sm font-medium">
                ${Number(opportunity.value || 0).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border shrink-0" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Weighted Value</span>
              <p className="text-sm font-medium">
                ${weightedValue.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border shrink-0" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Percent className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Probability</span>
              <p className="text-sm font-medium">
                {opportunity.probability || 0}%
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border shrink-0" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Assigned To</span>
              <p className="text-sm font-medium">
                {opportunity.salesRep
                  ? `${opportunity.salesRep.firstName || ""} ${
                      opportunity.salesRep.lastName || ""
                    }`.trim()
                  : "—"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {opportunity.notes && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="text-sm font-semibold mb-2">Notes</h2>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {opportunity.notes}
          </p>
        </div>
      )}

      <FormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit Opportunity"
        description="Update the opportunity information below."
        formId="edit-opportunity"
        isPending={updateMutation.isPending}
        submitLabel="Save Changes"
        size="lg"
        data-testid="drawer-edit-opportunity"
      >
        <Form {...form}>
          <form
            id="edit-opportunity"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Opportunity Name *</FormLabel>
                  <FormControl>
                    <Input
                      variant="flat"
                      placeholder="Q1 2026 Packaging Refresh"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estimated Value *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          $
                        </span>
                        <Input
                          variant="flat"
                          type="number"
                          step="0.01"
                          min="0"
                          className="pl-7"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="expectedCloseDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Expected Close Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground",
                            )}
                          >
                            {field.value
                              ? format(field.value, "MMM d, yyyy")
                              : "Pick a date"}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="stage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stage *</FormLabel>
                    <Select
                      onValueChange={(v) => {
                        field.onChange(v);
                        handleStageChange(v);
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger variant="flat">
                          <SelectValue placeholder="Select stage" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {salesStages.map((s) => (
                          <SelectItem key={s.id} value={s.name}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="probability"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Probability (%)</FormLabel>
                    <FormControl>
                      <Input variant="flat" type="number" min="0" max="100" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea variant="flat" placeholder="Additional details..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
